import { ReactNode, useEffect } from 'react';
import { Link, useLocation } from 'react-router';

import { cn, StatusBadge, StatusBadgeTone } from '@/components/ui';
import { useAuth } from '@/context/appContext';
import { useMenuBar } from '@/services/MenuBarService';
import { LinkTarget, NavItemType } from '@/types/menu';

import { navItemCheckAuth } from './index';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export interface NavItemProps {
  item: NavItemType;
  level: number;
}

export const NavItem = ({ item, level }: NavItemProps) => {
  const { hasAuth } = useAuth();
  // Select narrowly: a row should re-render only when its own state changes,
  // not on every menu-store update (there can be a hundred queue rows).
  const isSelected = useMenuBar(state => item.id !== undefined && state.openItem.includes(item.id));
  const setActiveItem = useMenuBar(state => state.setActiveItem);
  const drawerOpen = useMenuBar(state => state.drawerOpen);
  const storeChip = useMenuBar(state => (item.id !== undefined ? state.chips[item.id] : undefined));

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const Icon = item.icon!;
  const itemIcon = item.icon ? <Icon className="size-icon" /> : null;

  const { pathname } = useLocation();

  // Mark this item active when the page is its url (or nested under it, for matchNested items).
  const { id: itemId, url: itemUrl, matchNested } = item;
  useEffect(() => {
    const itemPath = itemUrl?.replace(/\?.*$/, '').replace(/#.*/, '');
    if (itemId && pathname && (pathname === itemPath || (matchNested && pathname.startsWith(itemPath + '/')))) {
      setActiveItem([itemId]);
    }
  }, [pathname, itemId, itemUrl, matchNested, setActiveItem]);

  const itemChip = item.chip ? item.chip : (storeChip ?? null);

  if (!navItemCheckAuth(item, hasAuth)) {
    return null;
  }

  if (item.render) {
    return item.render({ item, level });
  }

  const chipTone: StatusBadgeTone = itemChip?.color ?? 'neutral';
  const label = typeof item.title === 'string' ? item.title : undefined;
  const hoverTitle = item.disabled
    ? typeof item.caption === 'string'
      ? item.caption
      : 'Coming soon'
    : !drawerOpen
      ? label
      : undefined;
  // Level-1 labels stay mounted so the aside clip can reveal them. Open uses the
  // compact icon slot (staging layout); collapsed uses the mini-rail width.
  const showLevel1Icon = level === 1 && !!itemIcon;
  const showLabels = level !== 1 || !!itemIcon || drawerOpen;
  const content: ReactNode = (
    <>
      {showLevel1Icon && (
        <span
          className={cn('relative flex shrink-0 items-center justify-center', drawerOpen ? 'size-8' : 'w-mini-drawer')}
        >
          <span className="relative flex size-8 items-center justify-center">
            {itemIcon}
            {!drawerOpen && itemChip && (
              <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-caution px-1 text-center text-micro font-semibold leading-none text-on-caution">
                {itemChip.label}
              </span>
            )}
          </span>
        </span>
      )}
      {level !== 1 && itemIcon && (
        <span className="relative flex size-8 shrink-0 items-center justify-center">{itemIcon}</span>
      )}
      {showLabels && (
        <span
          className={cn(
            'min-w-0 flex-1 text-left text-nav font-medium',
            // String titles ellipsis here; ReactNode titles (e.g. queue rows with a
            // presence dot) manage their own truncate so absolute/flex prefixes work.
            typeof item.title === 'string' ? 'truncate' : 'flex items-center overflow-hidden',
          )}
          aria-hidden={level === 1 && !drawerOpen ? true : undefined}
        >
          {item.title}
        </span>
      )}
      {showLabels && (
        <span
          className="flex shrink-0 items-center justify-end gap-2"
          aria-hidden={level === 1 && !drawerOpen ? true : undefined}
        >
          {item.iconRight}
          {itemChip && <StatusBadge tone={chipTone}>{itemChip.label}</StatusBadge>}
        </span>
      )}
    </>
  );
  const className = cn(
    // Keep nav rows in the aside stacking context — a high z-index here can paint
    // over main content if the drawer and page margins ever disagree.
    'group relative flex w-full items-center gap-2 min-h-9 rounded-auditor py-0.5 no-underline transition-colors',
    showLabels && 'pr-2',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sidebar-ink/70',
    // Keep hover so disabled "Coming soon" titles still show; block navigation below.
    item.disabled && 'cursor-default opacity-45',
    isSelected
      ? 'bg-sidebar-active text-sidebar-ink shadow-auditor'
      : 'text-sidebar-ink/80 hover:bg-sidebar-ink/10 hover:text-sidebar-ink',
    // Open drawer: same indents as before the rail work (px-3 nav + pl-1 / pl-nav-*).
    (drawerOpen || level !== 1)
      && {
        1: itemIcon ? 'pl-1' : 'pl-3',
        2: 'pl-nav-1',
        3: 'pl-nav-2',
      }[level],
  );

  if (item.onClick) {
    return (
      <button type="button" className={className} disabled={item.disabled} onClick={item.onClick} title={hoverTitle}>
        {content}
      </button>
    );
  }

  if (item.external && item.url) {
    return (
      <a
        className={className}
        href={item.disabled ? undefined : item.url}
        target={itemTarget}
        title={hoverTitle}
        aria-disabled={item.disabled || undefined}
        onClick={
          item.disabled
            ? event => {
                event.preventDefault();
              }
            : undefined
        }
      >
        {content}
      </a>
    );
  }

  if (item.url) {
    return (
      <Link
        className={className}
        to={item.url}
        target={itemTarget}
        title={hoverTitle}
        aria-disabled={item.disabled || undefined}
        tabIndex={item.disabled ? -1 : undefined}
        onClick={
          item.disabled
            ? event => {
                event.preventDefault();
              }
            : undefined
        }
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={className} title={hoverTitle} aria-disabled={item.disabled || undefined}>
      {content}
    </div>
  );
};
